import { useEffect, useState } from "react";
import type {
  FilterColor,
  VirtualFolderDto,
  VirtualFolderRuleType,
} from "../../../shared/types";
import { useSaveVirtualFolder } from "../../hooks/useApi";
import { FILTER_COLORS, SWATCH_CLASSES } from "../filterColors";
import { senderLabel } from "../filters/CreateFilterDialog";

const RULE_MODES: { value: VirtualFolderRuleType; label: string }[] = [
  { value: "none", label: "Manual" },
  { value: "addresses", label: "From addresses" },
  { value: "ai", label: "AI prompt" },
];

const inputClass =
  "w-full rounded border border-stroke px-2.5 py-1.5 text-[13px] outline-none focus:border-accent";
const labelClass = "block pb-1 pt-2 text-[12px] font-medium text-ink-muted";

/**
 * Create or rename a virtual folder (DB-only — the provider mailbox is never
 * touched). Opened from the sidebar FOLDERS "+" or the folder view's pencil.
 */
export function CreateVirtualFolderDialog({
  folder,
  prefillAddress,
  onClose,
  onSaved,
}: {
  /** Set when renaming/recoloring an existing folder. */
  folder?: VirtualFolderDto;
  /** "Create folder" from a mail: pre-select the address rule for this sender. */
  prefillAddress?: string;
  onClose: () => void;
  onSaved: (folderId?: number) => void;
}) {
  const save = useSaveVirtualFolder();
  const [name, setName] = useState(
    folder?.name ?? (prefillAddress ? senderLabel(prefillAddress) : ""),
  );
  const [color, setColor] = useState<FilterColor>(folder?.color ?? "blue");
  const [ruleType, setRuleType] = useState<VirtualFolderRuleType>(
    folder?.ruleAddresses.length || prefillAddress
      ? "addresses"
      : folder?.rulePrompt
        ? "ai"
        : "none",
  );
  const [addresses, setAddresses] = useState(
    folder ? (folder.ruleAddresses ?? []).join("\n") : (prefillAddress ?? ""),
  );
  const [prompt, setPrompt] = useState(folder?.rulePrompt ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError("Enter a folder name.");
    const addrList = addresses
      .split(/[\n,]/)
      .map((a) => a.trim())
      .filter(Boolean);
    if (ruleType === "addresses" && !addrList.length)
      return setError("Enter at least one email address.");
    if (ruleType === "ai" && !prompt.trim()) return setError("Enter an AI prompt.");
    try {
      const saved = await save.mutateAsync({
        id: folder?.id,
        name: name.trim(),
        color,
        ruleType,
        ruleAddresses: addrList,
        rulePrompt: prompt.trim(),
      });
      onSaved(saved.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/30"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-[400px] max-w-[92vw] rounded-lg border border-stroke bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={folder ? "Rename folder" : "New folder"}
      >
        <h2 className="text-[15px] font-semibold">{folder ? "Rename folder" : "New folder"}</h2>
        <p className="pt-1 text-[12px] text-ink-muted">
          Lives only in this app — drag mail onto it to organize. Your real mailbox is untouched.
        </p>

        <label className={labelClass}>Name</label>
        <input
          className={inputClass}
          autoFocus
          placeholder="Dori"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />

        <label className={labelClass}>Color</label>
        <div className="flex items-center gap-1.5">
          {FILTER_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              title={c}
              aria-label={`Color ${c}`}
              className={`size-4 rounded-full ${SWATCH_CLASSES[c]} ${
                color === c ? "ring-2 ring-accent ring-offset-1" : ""
              }`}
            />
          ))}
        </div>

        <label className={labelClass}>Auto-assign incoming mail</label>
        <div className="flex items-center gap-1">
          {RULE_MODES.map((m) => (
            <button
              key={m.value}
              onClick={() => setRuleType(m.value)}
              className={`rounded border px-2.5 py-1 text-[12px] ${
                ruleType === m.value
                  ? "border-accent bg-accent-subtle font-medium text-accent"
                  : "border-stroke hover:bg-black/5"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {ruleType === "addresses" && (
          <>
            <textarea
              className={`${inputClass} mt-2 h-16 resize-y`}
              placeholder={"noreply@aliexpress.com\nile.is — one per line"}
              value={addresses}
              onChange={(e) => setAddresses(e.target.value)}
            />
            <p className="pt-1 text-[11px] text-ink-muted">
              New inbox mail from these senders is moved here automatically. A plain domain
              (e.g. ile.is) catches every sender from it. Saving also scans your cached history.
            </p>
          </>
        )}
        {ruleType === "ai" && (
          <>
            <textarea
              className={`${inputClass} mt-2 h-20 resize-y`}
              placeholder="Order confirmations, package tracking and delivery notifications from web shops…"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
            <p className="pt-1 text-[11px] text-ink-muted">
              New inbox mail matching this description is moved here automatically. Saving also
              scans your cached history (uses AI, costs a fraction of a cent per batch).
            </p>
          </>
        )}

        {error && <p className="pt-2 text-[12px] text-red-700">{error}</p>}

        <div className="flex items-center gap-2 pt-4">
          <button
            onClick={() => void submit()}
            disabled={save.isPending}
            className="rounded bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : folder ? "Save" : "Create"}
          </button>
          <button
            onClick={onClose}
            className="rounded border border-stroke px-4 py-1.5 text-[13px] hover:bg-black/5"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
